import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { LoaderCircle } from 'lucide-react';
import { Link } from 'react-router';
import { useCollaborationNotes } from '../hooks/useCollaborationQueries';
import ProtectedAttachment from './ProtectedAttachment';
import PendingCollaborationNote from './PendingCollaborationNote';

const readable = (html = '') => {
  if (typeof document === 'undefined') return html.replace(/<[^>]*>/g, ' ');
  const node = document.createElement('div');
  node.innerHTML = html;
  return node.textContent || '';
};

const formatDate = (value) => value
  ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
  : '';

export default function CollaborationTimeline({ collaborationId, outgoing = [], onRetry }) {
  const sentinel = useRef(null);
  const timeline = useRef(null);
  const initialScrollId = useRef(null);
  const previousOutgoingCount = useRef(0);
  const olderPageScroll = useRef(null);
  const followLatest = useRef(true);
  const notes = useCollaborationNotes(collaborationId);
  const entries = useMemo(
    () => (notes.data?.pages || []).slice().reverse().flatMap((page) => page.items),
    [notes.data],
  );
  const visibleOutgoing = outgoing.filter((draft) => !entries.some((note) => note.clientRequestId === draft.clientRequestId));
  const latestNoteId = visibleOutgoing.at(-1)?.localId || entries.at(-1)?.id;

  useLayoutEffect(() => {
    if (!notes.isPending && timeline.current && initialScrollId.current !== collaborationId) {
      followLatest.current = true;
      timeline.current.scrollTop = timeline.current.scrollHeight;
      initialScrollId.current = collaborationId;
    }
  }, [collaborationId, notes.isPending, entries.length]);

  useLayoutEffect(() => {
    if (outgoing.length > previousOutgoingCount.current && timeline.current) {
      followLatest.current = true;
      timeline.current.scrollTop = timeline.current.scrollHeight;
    }
    previousOutgoingCount.current = outgoing.length;
  }, [outgoing.length]);

  useEffect(() => {
    const latest = timeline.current?.querySelector('.collab-note:last-of-type');
    if (!latest || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (followLatest.current && !olderPageScroll.current && timeline.current) {
        timeline.current.scrollTop = timeline.current.scrollHeight;
      }
    });
    observer.observe(latest);
    return () => observer.disconnect();
  }, [collaborationId, latestNoteId]);

  const fetchOlder = useCallback(() => {
    if (!timeline.current || notes.isFetching) return;
    olderPageScroll.current = {
      height: timeline.current.scrollHeight,
      top: timeline.current.scrollTop,
      pageCount: notes.data?.pages?.length || 0,
    };
    void notes.fetchNextPage();
  }, [notes.data?.pages?.length, notes.fetchNextPage, notes.isFetching]);

  useLayoutEffect(() => {
    const snapshot = olderPageScroll.current;
    if (!snapshot || notes.isFetchingNextPage) return;
    if ((notes.data?.pages?.length || 0) > snapshot.pageCount && timeline.current) {
      timeline.current.scrollTop = snapshot.top + timeline.current.scrollHeight - snapshot.height;
      olderPageScroll.current = null;
    } else if (notes.isFetchNextPageError) {
      olderPageScroll.current = null;
    }
  }, [notes.data?.pages?.length, notes.isFetchingNextPage, notes.isFetchNextPageError]);

  useEffect(() => {
    if (!notes.hasNextPage || notes.isFetching || !sentinel.current || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) fetchOlder();
    });
    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [notes.hasNextPage, notes.isFetching, fetchOlder]);

  return (
    <div className="collab-timeline" ref={timeline} onScroll={(event) => {
      const element = event.currentTarget;
      followLatest.current = element.scrollHeight - element.clientHeight - element.scrollTop <= 48;
    }}>
      {notes.isPending && <div className="collab-state">Loading notes…</div>}
      {notes.isError && <div className="collab-state collab-state--error">Unable to load notes.</div>}
      {notes.hasNextPage && (
        <div ref={sentinel} className="collab-load-more" aria-label="Load older notes">
          <LoaderCircle size={18} className={notes.isFetchingNextPage ? 'collab-load-more__spinning' : ''} aria-hidden="true" />
          <span>{notes.isFetchingNextPage ? 'Loading older notes…' : 'Load older notes'}</span>
        </div>
      )}
      {notes.isFetchNextPageError && <button type="button" className="collab-button collab-button--ghost" onClick={fetchOlder}>Retry loading older notes</button>}
      {!notes.isPending && !notes.isError && entries.length === 0 && visibleOutgoing.length === 0 && <div className="collab-state">No shared notes yet.</div>}
      {entries.map((note) => (
        <article className="collab-note" key={note.id}>
          <div className="collab-note__meta">
            <strong><span className="collab-note__avatar" aria-hidden="true">{(note.author?.name || 'Doctor').replace('Dr. ', '').split(/\s+/).map((part) => part[0]).slice(0, 2).join('')}</span>{note.author?.name || 'Doctor'}</strong>
            <time dateTime={note.createdAt}>{formatDate(note.createdAt)}</time>
          </div>
          {note.text && <p>{readable(note.text)}</p>}
          {note.appointmentId && (
            <div className="collab-note__appointment">
              <span>Sent from appointment #{note.appointmentId}</span>
              <Link to={`/doctor/appointment-details/${note.appointmentId}`}>Go to appointment</Link>
            </div>
          )}
          {(note.attachments || []).map((attachment) => <ProtectedAttachment key={attachment.id} attachment={attachment} />)}
        </article>
      ))}
      {visibleOutgoing.map((draft) => <PendingCollaborationNote key={draft.localId} draft={draft} onRetry={onRetry} />)}
    </div>
  );
}
